import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  addCollarBookmark,
  addCourseBookmark,
  addVideoView,
  getCollarVideosDetail,
  getCourseTopics,
  removeCollarBookmark,
  removeCourseBookmark,
} from "../services/courseDetailService";
import {
  NavigateNullMsisdn,
  getDataFromCookies,
} from "../utils/CommonFunction";
import tagManagerEvents from "../utils/GoogleTagManager";
import type {
  Chapter,
  CollarVideo,
  CourseVideo,
} from "../types/courseDetail";

const DEFAULT_TITLE = "Course Details";

export interface CourseDetailState {
  isLoading: boolean;
  courseId: number;
  chapterId: number;
  courseVideoId: number;
  videoId: number;
  utmSource: string;
  token: string | null;

  topics: Chapter | null;
  currentVideo: Chapter | null;
  currentCollar: CollarVideo | undefined;
  nextCollar: CollarVideo[];
  searchCollar: CollarVideo[];
  searchCourseVideo: CourseVideo[];
  searchTerm: string;
  showError: boolean;

  setSearchTerm: (value: string) => void;
  runSearch: (term: string) => void;
  toggleCourseBookmark: (chapter: Chapter) => void;
  toggleCollarBookmark: () => void;
}

const parseId = (value: string | null): number => parseInt(value ?? "", 10);

/**
 * Encapsulates the entire Course Detail flow: topic-tree loading and flattening,
 * collar-video loading, search, and the course/collar bookmark toggles.
 *
 * The topic-tree flattening mirrors the original screen (including augmenting
 * each video object in place and sharing the same `chapter` reference between
 * `topics` and `currentVideo`), so search-over-`nextVideo` keeps working.
 */
export const useCourseDetail = (
  token: string | null,
  loaderStatus: (status: boolean) => void
): CourseDetailState => {
  const [searchParams] = useSearchParams();
  const [courseId] = useState(() => parseId(searchParams.get("courseId")));
  const [videoId] = useState(() => parseId(searchParams.get("videoId")));
  const [chapterId] = useState(() => parseId(searchParams.get("chapterId")));
  const [courseVideoId, setCourseVideoId] = useState(() =>
    parseId(searchParams.get("courseVideoId"))
  );
  const utmSource = searchParams.get("utm_source") ?? "";

  const { cookieMsisdn, cookieSubscribeId } = getDataFromCookies();

  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [topics, setTopics] = useState<Chapter | null>(null);
  const [currentVideo, setCurrentVideo] = useState<Chapter | null>(null);
  const [currentCollar, setCurrentCollar] = useState<CollarVideo | undefined>();
  const [nextCollar, setNextCollar] = useState<CollarVideo[]>([]);
  const [allCollar, setAllCollar] = useState<CollarVideo[]>([]);
  const [searchCollar, setSearchCollar] = useState<CollarVideo[]>([]);
  const [searchCourseVideo, setSearchCourseVideo] = useState<CourseVideo[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getTopicByChapter = useCallback(
    async (id: number, chId: number): Promise<void> => {
      try {
        const data = await getCourseTopics(id, cookieSubscribeId);
        if (data.status === 200) {
          const chapter = data.response.chapters.filter(
            (item) => item.id === chId
          )[0];
          chapter.level = data.response.level;
          chapter.Subject = data.response.subject;
          setTopics(chapter);

          if (chapter.topics.length > 0) {
            // Default to the first available video when none was requested.
            let firstVideoId: number | undefined;
            chapter.topics.forEach((topic) => {
              if (topic.videos.length > 0) {
                firstVideoId = topic.videos[0].id;
                return;
              }
              topic.subTopic.forEach((sub) => {
                if (sub.videos.length > 0) {
                  firstVideoId = sub.videos[0].id;
                }
              });
            });

            const selectedVideoId = courseVideoId || firstVideoId;
            if (!courseVideoId && firstVideoId) {
              setCourseVideoId(firstVideoId);
            }

            const subject = chapter.Subject;
            const nextVideo: CourseVideo[] = [];
            chapter.nextVideo = nextVideo;
            chapter.video = null;

            chapter.topics.forEach((topic) => {
              topic.videos.forEach((video) => {
                video.topicId = topic.id;
                video.questions = topic.questions;
                video.topicNumber = topic.topicNumber;
                video.chapterId = chapter.id;
                video.Subject = subject;
                video.topicTitle = topic.title;
                video.textbookUrl = topic.textbookUrl;
                if (video.id === selectedVideoId) {
                  chapter.video = video;
                } else {
                  nextVideo.push(video);
                }
              });
              topic.subTopic.forEach((sub) => {
                sub.videos.forEach((video) => {
                  video.subTopicId = sub.id;
                  video.questions = sub.questions;
                  video.topicNumber = sub.topicNumber;
                  video.chapterId = chapter.id;
                  video.Subject = subject;
                  video.topicTitle = sub.title;
                  video.textbookUrl = sub.textbookUrl;
                  if (video.id === selectedVideoId) {
                    chapter.video = video;
                  } else {
                    nextVideo.push(video);
                  }
                });
              });
            });

            setCurrentVideo(chapter);
          }
        } else {
          setShowError(true);
        }
      } catch (error) {
        setShowError(true);
      }
      loaderStatus(true);
      setIsLoading(false);
    },
    [cookieSubscribeId, courseVideoId, loaderStatus]
  );

  const getCollar = useCallback(async (): Promise<void> => {
    try {
      setNextCollar([]);
      const data = await getCollarVideosDetail(courseId, cookieSubscribeId);
      if (data.status === 200) {
        tagManagerEvents("collar_videos_details All", utmSource);
        data.response.forEach((item) => {
          setAllCollar((next) => [...next, item]);
          if (item.id === videoId) {
            setCurrentCollar(item);
            void addVideoView(item.id, token);
            setTitle(item.title);
          } else {
            setNextCollar((next) => [...next, item]);
          }
        });
      } else {
        setShowError(true);
      }
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, [courseId, cookieSubscribeId, videoId, token, utmSource]);

  const runSearch = useCallback(
    (term: string): void => {
      if (term) {
        const needle = term.toLowerCase();
        const seen = new Set<number>();

        if (!chapterId && videoId) {
          const matches = allCollar.filter((item) => {
            const isMatch =
              item.title.toLowerCase().includes(needle) ||
              item.description.includes(needle);
            if (isMatch && !seen.has(item.id) && item.id !== videoId) {
              seen.add(item.id);
              return true;
            }
            return false;
          });
          setSearchCollar(matches);
        } else {
          const matches = (topics?.nextVideo ?? []).filter((item) => {
            const isMatch =
              item.title.toLowerCase().includes(needle) ||
              (item.topicTitle ?? "").toLowerCase().includes(needle);
            if (isMatch && !seen.has(item.id)) {
              seen.add(item.id);
              return true;
            }
            return false;
          });
          setSearchCourseVideo(matches);
        }
      } else {
        setSearchCourseVideo([]);
        setSearchCollar([]);
      }
      loaderStatus(true);
    },
    [chapterId, videoId, allCollar, topics, loaderStatus]
  );

  // Re-run the search whenever the term (or the searchable data) changes.
  useEffect(() => {
    runSearch(searchTerm);
  }, [searchTerm, runSearch]);

  const toggleCourseBookmark = useCallback(
    async (chapter: Chapter): Promise<void> => {
      const video = chapter.video;
      if (!video) {
        return;
      }
      if (video.bookmark) {
        const data = await removeCourseBookmark({
          msisdn: cookieMsisdn,
          bookmarkUrl: "",
          id: video.bookmarkId,
          token,
        });
        if (data.status === 200) {
          setCurrentVideo((prev) =>
            prev && prev.video
              ? { ...prev, video: { ...prev.video, bookmark: false } }
              : prev
          );
        }
      } else {
        const data = await addCourseBookmark({
          msisdn: cookieMsisdn,
          category: "Video",
          key: "",
          title: video.title,
          courseName: video.Subject?.title ?? "",
          bookmarkUrl: "",
          chapterId: video.chapterId ?? 0,
          courseId,
          videoId: video.id,
          token,
        });
        if (data.status === 200) {
          tagManagerEvents("add_favourites", utmSource);
          setCurrentVideo((prev) =>
            prev && prev.video
              ? {
                  ...prev,
                  video: {
                    ...prev.video,
                    bookmark: true,
                    bookmarkId: data.bookmarkId ?? prev.video.bookmarkId,
                  },
                }
              : prev
          );
        }
      }
    },
    [cookieMsisdn, courseId, token, utmSource]
  );

  const toggleCollarBookmark = useCallback(async (): Promise<void> => {
    if (!currentCollar) {
      return;
    }
    try {
      if (currentCollar.bookmark) {
        const res = await removeCollarBookmark({
          subscriberId: cookieSubscribeId,
          collarVideoId: currentCollar.id,
          token,
        });
        if (res.status === 200) {
          setCurrentCollar((prev) => (prev ? { ...prev, bookmark: false } : prev));
        }
      } else {
        const res = await addCollarBookmark({
          subscriberId: cookieSubscribeId,
          collarVideoId: currentCollar.id,
          collarCourseId: currentCollar.course.id,
          token,
        });
        if (res.status === 201) {
          tagManagerEvents("add_Favourite", utmSource);
          setCurrentCollar((prev) => (prev ? { ...prev, bookmark: true } : prev));
        }
      }
    } catch (error) {
      console.warn("error in post", error);
    }
  }, [currentCollar, cookieSubscribeId, token, utmSource]);

  // Routing: choose which data to load based on the URL params.
  useEffect(() => {
    // NavigateNullMsisdn();
    if (token !== null) {
      if (chapterId && courseId && courseVideoId) {
        loaderStatus(true);
        void getTopicByChapter(courseId, chapterId);
      } else if (courseId && videoId) {
        loaderStatus(true);
        void getCollar();
      } else if (chapterId && courseId) {
        loaderStatus(true);
        void getTopicByChapter(courseId, chapterId);
      } else {
        window.location.href = "/";
      }
    }
    document.title = title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, token]);

  return {
    isLoading,
    courseId,
    chapterId,
    courseVideoId,
    videoId,
    utmSource,
    token,
    topics,
    currentVideo,
    currentCollar,
    nextCollar,
    searchCollar,
    searchCourseVideo,
    searchTerm,
    showError,
    setSearchTerm,
    runSearch,
    toggleCourseBookmark,
    toggleCollarBookmark,
  };
};
